import { isNil } from 'lodash-unified'
import { buildProps, definePropType, isNumber } from '@zoryn/utils'
import { useAriaProps, useSizeProp } from '@zoryn/hooks'
import {
  CHANGE_EVENT,
  INPUT_EVENT,
  UPDATE_MODEL_EVENT,
} from '@zoryn/constants'

import type { ExtractPublicPropTypes, HTMLAttributes } from 'vue'
import type { ComponentSize } from '@zoryn/constants'
import type {
  GridColumnBeforeOpenFn,
  GridInputTablePickerOptions,
  GridOptions,
} from '../../grid/src/types'
import type InputTable from './input-table.vue'

/** @deprecated 使用 InputTableOptions */
export type ProxyOptions = InputTableOptions

/** @deprecated beforeOpen 回调参数已改为当前行 row */
export interface InputTableBeforeOpenContext {
  source: Record<string, unknown>
  map: Record<string, string>
  options: InputTableOptions
}

export type InputTableBeforeOpenFn = GridColumnBeforeOpenFn

/** ZInputTable 弹窗内 ZGrid 配置 */
export type InputTableOptions = GridInputTablePickerOptions &
  Partial<Pick<GridOptions, 'toolbar' | 'editable' | 'newBtn' | 'delBtn'>> & {
    /** 打开弹窗前校验，参数为当前行 row（source），返回 false 则不打开 */
    beforeOpen?: InputTableBeforeOpenFn
  }

export const INPUT_TABLE_READONLY_OPTIONS: Partial<GridOptions> = {
  editable: false,
  newBtn: false,
  delBtn: false,
  toolbar: false,
}

/** 弹窗内 ZGrid 使用独立配置对象，避免与列级 GridOption 实例共享引用 */
export function resolveInputTableGridOptions(
  opts: InputTableOptions | GridOptions | undefined
): GridOptions {
  const source = (opts ?? { columns: [] }) as GridOptions
  const gridOptions: GridOptions = {
    ...INPUT_TABLE_READONLY_OPTIONS,
    module: source.module ?? '',
    columns: source.columns ?? [],
    editable: false,
    newBtn: false,
    delBtn: false,
    toolbar: source.toolbar ?? false,
    queryByExamplePage: async () => {},
    getCurrentRow: () => null,
    getAllCheckbox: () => [],
  }

  if (source.data != null) {
    gridOptions.data = source.data
  }
  if (source.params != null) {
    gridOptions.params = { ...source.params }
  }
  if (source.beforeQuery) {
    gridOptions.beforeQuery = source.beforeQuery
  }
  const lovType = (source as GridOptions & { lovType?: string }).lovType
  if (lovType) {
    ;(gridOptions as GridOptions & { lovType?: string }).lovType = lovType
  }
  if (source.valueField) {
    gridOptions.valueField = source.valueField
  }
  if (source.autoQuery != null) {
    gridOptions.autoQuery = source.autoQuery
  }
  if (source.queryUrl) {
    gridOptions.queryUrl = source.queryUrl
  }
  if (source.distinctColumnUrl) {
    gridOptions.distinctColumnUrl = source.distinctColumnUrl
  }
  if (source.parent) {
    gridOptions.parent = source.parent
  }
  if (source.headId) {
    gridOptions.headId = source.headId
  }

  return gridOptions
}

export function resolveInputTableValueField(
  options?: Pick<InputTableOptions, 'valueField'>,
  map?: Record<string, string>
): string {
  if (options?.valueField) {
    return options.valueField
  }
  if (!map || !Object.keys(map).length) {
    return 'id'
  }
  const selfNamed = Object.entries(map).find(
    ([sourceKey, pickerField]) => sourceKey === pickerField
  )
  if (selfNamed) {
    return selfNamed[1]
  }
  const idEntry = Object.entries(map).find(([, pickerField]) => pickerField === 'id')
  if (idEntry) {
    return 'id'
  }
  const displayEntry = Object.entries(map).find(
    ([sourceKey, pickerField]) =>
      pickerField !== 'id' && sourceKey !== 'id' && !sourceKey.endsWith('Id')
  )
  if (displayEntry) {
    return displayEntry[1]
  }
  return 'id'
}

/** map 中 picker 列 === valueField 时，对应的 source 字段名 */
export function resolveInputTableValueSourceKey(
  map: Record<string, string>,
  valueField: string
): string | undefined {
  return Object.entries(map).find(([, pickerField]) => pickerField === valueField)?.[0]
}

/** 按 map 清空 source 上各映射字段 */
export function clearInputTableMappedSource(
  source: Record<string, unknown> | undefined,
  map: Record<string, string> | undefined,
  emptyValue: null = null
) {
  if (!source || !map) {
    return
  }
  for (const key in map) {
    source[key] = emptyValue
  }
}

export interface InputTableProps {
  id?: string
  /** source 字段 -> 弹窗表格列字段 */
  map: Record<string, string>
  /** 选中后映射写入的目标对象 */
  source: Record<string, unknown>
  /** 弹窗内 ZGrid 配置 */
  options: InputTableOptions
  /** 打开弹窗前校验，参数为当前行 row（source），返回 false 则不打开（优先于 options.beforeOpen） */
  beforeOpen?: InputTableBeforeOpenFn
  step?: number
  stepStrictly?: boolean
  max?: number
  min?: number
  modelValue?: string | number | null
  /** 输入框展示文案（与表格单元格默认渲染一致；未传时按 map / lovType 解析） */
  displayText?: string
  /** 是否可清空；清空时同步重置 map 对应 source 字段 */
  clearable?: boolean
  readonly?: boolean
  disabled?: boolean
  size?: ComponentSize
  controls?: boolean
  controlsPosition?: '' | 'right'
  valueOnClear?: 'min' | 'max' | number | null
  name?: string
  placeholder?: string
  precision?: number
  validateEvent?: boolean
  ariaLabel?: string
  inputmode?: HTMLAttributes['inputmode']
  align?: 'left' | 'right' | 'center'
  disabledScientific?: boolean
  formatter?: (value: string) => string
  parser?: (value: string) => string
  tabindex?: string | number
}

/**
 * @deprecated Removed after 3.0.0, Use `InputTableProps` instead.
 */
export const inputTableProps = buildProps({
  id: {
    type: String,
    default: undefined,
  },
  map: {
    type: Object,
    default: {},
  },
  source: {
    type: Object,
  },
  options: {
    type: Object,
    required: true,
  },
  beforeOpen: {
    type: Function,
  },
  step: {
    type: Number,
    default: 1,
  },
  stepStrictly: Boolean,
  max: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER,
  },
  min: {
    type: Number,
    default: Number.MIN_SAFE_INTEGER,
  },
  modelValue: {
    type: [String, Number, null],
  },
  displayText: {
    type: String,
    default: undefined,
  },
  clearable: Boolean,
  readonly: Boolean,
  disabled: {
    type: Boolean,
    default: undefined,
  },
  size: useSizeProp,
  controls: {
    type: Boolean,
    default: true,
  },
  controlsPosition: {
    type: String,
    default: '',
    values: ['', 'right'],
  },
  valueOnClear: {
    type: definePropType<'min' | 'max' | number | null>([String, Number, null]),
    validator: (val: 'min' | 'max' | number | null) =>
      val === null || isNumber(val) || ['min', 'max'].includes(val),
    default: null,
  },
  name: String,
  placeholder: {
    type: String,
    default: 'Please select',
  },
  precision: {
    type: Number,
    validator: (val: number) =>
      val >= 0 && val === Number.parseInt(`${val}`, 10),
  },
  validateEvent: {
    type: Boolean,
    default: true,
  },
  ...useAriaProps(['ariaLabel']),
  inputmode: {
    type: definePropType<HTMLAttributes['inputmode']>(String),
    default: undefined,
  },
  align: {
    type: definePropType<'left' | 'right' | 'center'>(String),
    default: 'center',
  },
  disabledScientific: Boolean,
  formatter: {
    type: Function,
  },
  parser: {
    type: Function,
  },
  tabindex: {
    type: [String, Number],
    default: 0,
  },
} as const)

/**
 * @deprecated Removed after 3.0.0, Use `InputTableProps` instead.
 */
export type InputTablePropsPublic = ExtractPublicPropTypes<
  typeof inputTableProps
>

export const inputTableEmits = {
  [CHANGE_EVENT]: (
    cur: string | number | undefined,
    prev: string | number | undefined
  ) => prev !== cur,
  blur: (e: FocusEvent) => e instanceof FocusEvent,
  focus: (e: FocusEvent) => e instanceof FocusEvent,
  [INPUT_EVENT]: (val: string | number | null | undefined) =>
    typeof val === 'string' || isNumber(val) || isNil(val),
  [UPDATE_MODEL_EVENT]: (val: string | number | undefined) =>
    typeof val === 'string' || isNumber(val) || isNil(val),
  clear: () => true,
}
export type InputTableEmits = typeof inputTableEmits

export type InputTableInstance = InstanceType<typeof InputTable> & unknown
